import {
  ArrowDownToLineIcon,
  ArrowUpToLineIcon,
  CloseIcon,
  defaultTextVariants,
  IconButton,
  Text,
  Tooltip,
} from "@carbon6/c6-frontend-design";
import { useLayoutEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useWindowTaskbarPositionPreference } from "./taskbarPosition";
import type { WindowEntry } from "./windowStackManager";
import { WINDOW_TASKBAR_Z } from "./windowLayers";
import styles from "./WindowTaskbar.module.css";

type WindowTaskbarProps = {
  // 열린 모든 전체화면 창이다(활성+최소화). 작업표시줄은 모두 나열한다.
  entries: readonly WindowEntry[];
  // 지금 전체화면으로 보이는 창이다. 그 항목을 강조(눌린 상태)한다. null 이면 모두 배경(최소화)이다.
  activeId: string | null;
  // 본문 클릭이다. 호스트가 분기한다. 활성 항목이면 최소화로 토글하고, 아니면 그 창을 전체화면으로 전환한다.
  onSelect: (id: string) => void;
  // [X] 를 누르면 그 창을 닫는다.
  onClose: (id: string) => void;
  ariaLabel?: string;
};

function renderTaskbarLabel(entry: WindowEntry): ReactNode {
  return (
    entry.label ?? (
      <Text variant={defaultTextVariants["caption-bold"]} className={styles.labelText}>
        {entry.title}
      </Text>
    )
  );
}

// 열린 전체화면 창들을 보여 주는 전역 작업표시줄이다(화면 상단/하단에 도킹하는 full-width 막대).
// OS 작업표시줄처럼 활성 창이 전체화면일 때도 항상 보인다(z-index 가 오버레이보다 높다). 각 항목을
// 클릭하면 창을 전환·토글하고, [X] 로 닫는다. 우측 끝 화살표 버튼으로 상단/하단 도킹을 전환한다
// (브라우저에 저장). 콘텐츠/스택 정책은 모르고 표시와 위임만 한다.
//
// body 로 portal 하는 이유가 있다. 활성 창(FullscreenWindow)이 c6 modalStack 에 등록되면 스택은
// body 직속 자식 중 z 가 낮은 요소를 inert 로 만든다. 막대가 앱 루트(#root) 안에 있으면 루트 전체와
// 함께 inert 가 되어 동작하지 않는다. 그래서 body 직속에 두고 창보다 높은 z(inline)를 준다. 그러면
// "모달 위 비-Modal 오버레이 보존" 규칙이 적용된다.
export function WindowTaskbar({
  entries,
  activeId,
  onSelect,
  onClose,
  ariaLabel = "열린 창",
}: WindowTaskbarProps): ReactNode {
  const [position, setPosition] = useWindowTaskbarPositionPreference();

  // 작업표시줄이 있는 동안 body 에 표시 속성을 둔다. 전역 CSS 가 이 속성을 보고 일반 페이지와
  // FullscreenWindow overlay 를 도킹 방향(상단/하단)에 맞춰 offset 만큼 옮긴다. 이 컴포넌트는
  // entries.length>0 일 때만 마운트된다.
  useLayoutEffect(() => {
    document.body.dataset.windowTaskbar = "";
    return () => {
      delete document.body.dataset.windowTaskbar;
    };
  }, []);

  useLayoutEffect(() => {
    document.body.dataset.windowTaskbarPosition = position;
    return () => {
      delete document.body.dataset.windowTaskbarPosition;
    };
  }, [position]);

  // 하단 도킹이면 툴팁 방향을 반대로 한다. 그래서 호버 툴팁이 화면 밖(막대 아래)이 아니라 막대 위에 표시된다.
  const tooltipPlacement = position === "bottom" ? "top" : "bottom";
  // 도킹 글리프는 누르면 막대가 이동할 방향을 가리킨다(상단 도킹=아래 막대, 하단 도킹=위 막대).
  // 툴팁 문구와 같은 방향이다. 현재 위치를 가리키면 이미 보이는 정보를 반복하고, 툴팁과도 어긋난다.
  const dockToggleLabel =
    position === "top"
      ? "작업 표시줄을 아래로 변경할 수 있습니다."
      : "작업 표시줄을 위로 변경할 수 있습니다.";

  return createPortal(
    <section
      className={position === "bottom" ? `${styles.taskbar} ${styles.bottom}` : styles.taskbar}
      style={{ zIndex: WINDOW_TASKBAR_Z }}
      aria-label={ariaLabel}
    >
      {entries.map((entry) => (
        <div
          key={entry.id}
          className={entry.id === activeId ? [styles.item, styles.active].join(" ") : styles.item}
        >
          {/* 항목이 좁으므로 이름은 말줄임된다. 호버하면 전체 데이터셋 이름을 툴팁으로 표시한다.
              c6 Tooltip Bubble 은 flex 라서 긴 토큰이 max-width 를 넘는다. 그래서 콘텐츠를 감싸 버블 안에서 줄바꿈한다. */}
          <Tooltip
            content={<span className={styles.tooltipText}>{entry.title}</span>}
            placement={tooltipPlacement}
            className={styles.selectTooltip}
          >
            <button
              type="button"
              className={styles.selectArea}
              aria-label={entry.title}
              aria-pressed={entry.id === activeId}
              onClick={() => onSelect(entry.id)}
            >
              <span className={styles.labelClip}>{renderTaskbarLabel(entry)}</span>
            </button>
          </Tooltip>
          {/* closable=false(예: 진행 중 업로드)면 [X] 를 숨긴다. 완료/폐기 전에는 닫을 수 없다. */}
          {entry.closable ? (
            <IconButton
              aria-label="종료"
              title="창을 종료합니다."
              variant="ghost"
              className={styles.closeButton}
              tone="primary"
              size="sm"
              border={false}
              onClick={() => onClose(entry.id)}
            >
              <CloseIcon />
            </IconButton>
          ) : null}
        </div>
      ))}
      {/* 우측 끝 도킹 토글이다. margin-left:auto 로 항목들과 분리해 오른쪽에 둔다. 여기서 c6 Tooltip 을
          쓰는 것은 작업표시줄에 허용된 예외다(HatTooltip 아님). */}
      <Tooltip
        content={<span className={styles.tooltipText}>{dockToggleLabel}</span>}
        placement={tooltipPlacement}
        className={styles.dockTooltip}
      >
        <IconButton
          aria-label={dockToggleLabel}
          variant="ghost"
          tone="primary"
          size="sm"
          border={false}
          className={styles.dockToggle}
          onClick={() => setPosition(position === "top" ? "bottom" : "top")}
        >
          {position === "top" ? <ArrowDownToLineIcon /> : <ArrowUpToLineIcon />}
        </IconButton>
      </Tooltip>
    </section>,
    document.body,
  );
}
